import { FC, useEffect, useState } from 'react';
import Modal from '@components/Modal';
import { Document } from '@components/DocumentForm';
import { nanoid } from 'nanoid';
import Button from '@components/DesignLibrary/Button';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { DatePicker, Select, Input, Checkbox } from '@/components/DesignLibrary/Atoms';
import * as format from '@format';
import { IncomeMetadata } from '@documentsApi/document-api-models';

import s from './AddIncomeSourceModal.module.scss';

type Props = {
  onClose: () => void;
  documentRequestUserId: string;
  refetchFiles: () => Promise<void>;
};

const AddIncomeSourceModal: FC<Props> = ({ onClose, documentRequestUserId, refetchFiles }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [document, setDocument] = useState<Document>({
    description: '',
    id: nanoid(),
    integrationFileName: null,
    integrationSlug: null,
    internalDataPromptHelp: null,
    internalDataType: null,
    isCollapsed: false,
    isForYourEyesOnly: false,
    isRequired: true,
    metadata: null,
    order: 1,
    title: '',
  });

  const [frequency, setFrequency] = useState<IncomeMetadata['payFrequency'] | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isEndDateRequired, setIsEndDateRequired] = useState(true);

  const { getData: addFileFn } = useNoodleApi(tsClient.documentRequests.createDocumentRequestUserFile);

  useEffect(() => {
    if (startDate && frequency) {
      setDocument(prev => ({
        ...prev,
        description: `${frequency ? `Pay Frequency: ${frequency}; ` : ''}${
          startDate ? `Start Date: ${format.datetimeLocalTZ.dateString(startDate)}` : ''
        }${(() => {
          if (isEndDateRequired) {
            if (endDate) {
              return `, End Date: ${format.datetimeLocalTZ.dateString(endDate)}`;
            }
            return '';
          }
          return ', still work there';
        })()}`,
        metadata: {
          endDate: endDate && isEndDateRequired ? format.datetimeLocalTZ.dateString(endDate) : null,
          payFrequency: frequency,
          startDate: format.datetimeLocalTZ.dateString(startDate),
        },
      }));
    }
  }, [startDate, endDate, isEndDateRequired, frequency]);

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    await addFileFn({
      ...document,
      id: documentRequestUserId,
    });
    await refetchFiles();
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Modal hideBottomInset title="Add Income Source" onClose={onClose}>
      <div className={s.container}>
        <Input
          value={document.title}
          onChange={val =>
            setDocument(prev => ({
              ...prev,
              title: val,
            }))
          }
          label="Employer name"
        />
        <Select
          label="Pay frequency"
          value={frequency}
          onChange={val => setFrequency(val as IncomeMetadata['payFrequency'])}
          options={[
            {
              key: 'Weekly',
              label: 'Weekly',
            },
            {
              key: 'Biweekly',
              label: 'Biweekly',
            },
            {
              key: 'Monthly',
              label: 'Monthly',
            },
            {
              key: 'Quarterly',
              label: 'Quarterly',
            },
            {
              key: 'Annually',
              label: 'Annually',
            },
          ]}
        />
        <DatePicker label="Start date" value={startDate} onChange={val => setStartDate(val)} />
        <Checkbox label="Still work there" isChecked={!isEndDateRequired} onChange={e => setIsEndDateRequired(!e)} />
        {isEndDateRequired && <DatePicker label="End date" value={endDate} onChange={val => setEndDate(val)} />}
        <Button loading={isSubmitting} disabled={!document.title || !frequency} variant="primary" size="md" fullWidth onClick={handleSubmit}>
          Add Income Source
        </Button>
      </div>
    </Modal>
  );
};

export default AddIncomeSourceModal;
